<template>
  <div class="sales-page-wrapper">
    <nav class="nav-bar">
      <div class="nav-content">
        <div class="logo">
          <img src="/images/target-icon-orange-min.png" alt="Content Commander Challenge" class="logo-image">
          <span>CONTENT COMMANDER CHALLENGE</span>
        </div>
        <button @click="scrollToPricing($event)" class="nav-cta-button">
          <i class="fas fa-rocket"></i> Start Building Now
        </button>
      </div>
    </nav>
    <header class="hero">
      <div class="hero-background"></div>
      <div class="hero-content">
        <img src="/images/aiclogo-small.png" alt="AICA Logo" class="hero-aica-logo">
        <h3 class="command-line-to-confidence">FROM COMMAND LINE TO CONFIDENCE:</h3><h1>Build Your First Real <br>AI-Powered Tool in 30 Days</h1>
        <p class="subtitle">Stop collecting knowledge.<br>Start building confidence.<br>Your first useful project awaits.</p>
        <p class="subtitle-secondary">You've learned the commands. You've seen the possibilities.<br><br>
Now build something real that you can actually use everyday—while mastering<br>the command line skills that separate no-code passengers from AI Captains.</p>
        <button @click="scrollToPricing($event)" class="cta-button hero-cta">Begin the Challenge</button>
      </div>
    </header>
    <div class="sales-page-content">

    <section class="hook">
      <h2>Ready for the Next Level?</h2>
      <p>You've completed the 5-Day Command Line Bootcamp. You felt that rush when your first command actually worked. You now understand what APIs and Git can do.</p>

<p><strong>But you still feel stuck between tutorials and real projects.</strong></p>

<p>You know the next step is to build something useful that will help you practice, but you're not sure how to bridge that gap from 'following instructions' to 'creating solutions.'</p>
<p>Watch exactly what you'll be building in the challenge:</p>
    
      <div class="video-container">
        <a href="https://youtu.be/LAQLB7aL2dw" target="_blank" rel="noopener noreferrer" class="video-thumbnail">
          <img src="/images/ccc2.jpg" alt="Content Commander Video" class="thumbnail-image">
          <div class="play-button">
            <i class="fas fa-play"></i>
          </div>
        </a>
        <div>
        <button @click="scrollToPricing($event)" class="cta-button" style="margin-top:6rem;">Join the Challenge</button>
        </div>
      </div>
      <img src="/images/gerry-test.png" style="margin-left:-40px; margin-top:80px">
    </section>




    <section class="features">
      <h2>Build A Foundation For Vibe Coding</h2>
      <p class="mb-4 subtitle" style="text-align:center;">The 30-lesson Content Commander Challenge is your structured, practical bridge from bootcamp graduate to confident builder—mastering command line, git, and API integration while creating something you'll use every day.</p>
      <div class="feature-grid">
        <div class="feature-card">
          <i class="fas fa-terminal feature-icon"></i>
          <h3>Practical Command Line Experience</h3>
          <p>This program was designed to make you comfortable with the command line while building an app you'll actually use.</p><p>No more toy projects—this is practical, daily-use software.</p>
        </div>
        <div class="feature-card">
          <i class="fas fa-ship feature-icon"></i>
          <h3>From Passenger to Captain</h3>
          <p>This is your bridge from being technically intimidated to technically confident.</p>
          <p>The tool you'll build is valuable, but the confidence to build whatever you want next? That's priceless.</p>
        </div>
        <div class="feature-card">
          <i class="fas fa-unlock feature-icon"></i>
          <h3>Beyond Walled Gardens</h3>
          <p>The moment you run your first successful script, everything changes.</p><p>No more being limited by what Zapier can connect. No more waiting for someone else to build the integration you need. You become the person who builds solutions.</p>
        </div>
      </div>
      
      <div class="by-day-30">
        <p class="by-day-30-text">By Day 30, you'll have:</p>
        <div class="by-day-30-items">
          <div class="by-day-30-item">
            <i class="fas fa-check"></i>
            <span>A working AI-powered application that you can actually use (your first real project!)</span>
          </div>
          <div class="by-day-30-item">
            <i class="fas fa-check"></i>
            <span>Unshakeable command line confidence<br>(20+ commands mastered)</span>
          </div>
          <div class="by-day-30-item">
            <i class="fas fa-check"></i>
            <span>The foundation to build whatever you imagine next (unlimited possibilities)</span>
          </div>
        </div>
      </div>
    </section>
    <div style="display: flex; justify-content: center; margin-top: 6rem; margin-left: auto; margin-right:auto;">
      <button @click="scrollToPricing($event)" class="cta-button">Get Started</button>
    </div>

    <section class="curriculum">
      <h2>For the Builder Who's Done Learning and Ready to Build</h2>
      <div class="timeline">
        <div class="timeline-item">
          <i class="fas fa-filter timeline-icon"></i>
          <h3>For the Right Builder</h3>
          <p>This challenge is for entrepreneurs & creators who are ready to stop watching tutorials and start building real projects.</p><p>You've learned the basics, but you know there's a gap between following instructions and creating solutions. You're ready to bridge that gap.</p>
        </div>
        <div class="timeline-item">
          <i class="fas fa-tools timeline-icon"></i>
          <h3>Transferable Skills</h3>
          <p>The command line confidence you build here unlocks every AI tool, framework, and platform you'll encounter.</p><p>This isn't just about Content Commander—it's about never feeling intimidated by a terminal again, no matter what you're building.</p>
        </div>
        <div class="timeline-item">
          <i class="fas fa-rocket timeline-icon"></i>
          <h3>Future-Proof Yourself</h3>
          <p>While others stay stuck in no-code limitations, you'll have the foundation to customize any AI agent, deploy any tool, and build exactly what you need.</p><p>The technical confidence gap is widening—make sure you're on the right side of it.</p>
        </div>
    
      </div>
      
    </section>

    <section class="what-you-build">
      <h2>What You'll Build</h2>
      <div class="feature-grid">
        <div class="grid-row">
          <div class="feature-card mb-5">
            <i class="fas fa-terminal feature-icon"></i>
            <h3>Command Line Confidence</h3>
            <p>Master the basics of terminal navigation and command execution</p>
          </div>
          <div class="feature-card">
            <i class="fas fa-plug feature-icon"></i>
            <h3>API Understanding</h3>
            <p>Learn how APIs connect services and enable automation</p>
          </div>
        </div>
        <div class="grid-row">
          <div class="feature-card mb-5">
            <i class="fas fa-code feature-icon"></i>
            <h3>Code Literacy</h3>
            <p>Start learning to read and modify common code structures</p>
          </div>
          <div class="feature-card">
            <i class="fas fa-robot feature-icon"></i>
            <h3>AI Integration</h3>
            <p>Develop a practical understanding of AI can be used in apps.</p>
          </div>
        </div>
      </div>
    </section>
    <section class="whats-included" v-show="false">
      <h2>The Complete Terminal Navigation Toolkit:</h2>
      <div class="feature-grid">
        <div class="feature-card">
          <i class="fas fa-rocket feature-icon"></i>
          <h3>Local Setup Guide</h3>
          <p>Before you begin your journey, you'll need to set up your development environment.</p>
          <p>The toolkit guides you through installing everything you need to start vibe coding.</p>
        </div>
        <div class="feature-card">
          <i class="fas fa-calendar-check feature-icon"></i>
          <h3>30-Day Challenge</h3>
          <p>A step-by-step guide to build the "Content Commander" App step by step.</p>
          <p>Each day adds a new feature while building your command line confidence.</p>
        </div>
        <div class="feature-card">
          <i class="fas fa-book feature-icon"></i>
          <h3>Command Line Cheat Sheet</h3>
          <p>Keep the essential commands at your fingertips.</p>
          <p>No need to Google basic commands – everything you need is right here.</p>
        </div>
      </div>
    </section>

    <!--
      Testimonials: migrated from Senja to Vouch (vouch.jordanurbs.com) on 2026-07-26.
      Baked into the component instead of the JS widget, so the quotes live in this
      app's own DOM rather than a cross-origin noindex'd iframe — JS-executing crawlers
      (Googlebot) can now read them. NOTE: this is a Vite SPA, so the text ships in the
      JS bundle, not dist/index.html; non-JS AEO bots (GPTBot, ClaudeBot) still see an
      empty shell. Prerendering/SSG would be needed to close that gap.
      Source: GET https://vouch.jordanurbs.com/api/wall/wall-of-love, filtered to the
      Content Commander Challenge testimonials. Re-fetch by id to refresh:
        87dafafb-3d31-4123-b911-4a6fae5aca0e (Gerry Albert)
        21c5dcb5-16f4-4696-b1bd-d6893bd42c24 (Desmond)
        9c00c2c0-abc4-4038-abbd-af5ccec31f45 (Peter Duffy, video)
    -->
    <section class="testimonials">
      <h2>What Students Are Saying</h2>
      <div class="testimonial-container">
        <figure
          v-for="t in testimonials"
          :key="t.id"
          class="vouch-card"
        >
          <div class="vouch-rating" v-if="t.rating" :aria-label="`${t.rating} out of 5 stars`">
            <i class="fas fa-star" v-for="n in t.rating" :key="n"></i>
          </div>
          <blockquote class="vouch-body">
            <p v-for="(para, i) in t.body" :key="i">{{ para }}</p>
          </blockquote>
          <figcaption class="vouch-author">
            <img
              v-if="t.avatar"
              :src="t.avatar"
              :alt="t.author"
              class="vouch-avatar"
              loading="lazy"
              width="44"
              height="44"
            />
            <span class="vouch-author-meta">
              <span class="vouch-author-name">{{ t.author }}</span>
              <span class="vouch-author-title" v-if="t.title">{{ t.title }}</span>
            </span>
          </figcaption>
          <a
            v-if="t.videoUrl"
            :href="t.videoUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="vouch-video-link"
          >
            <i class="fas fa-play-circle"></i> Watch the video
          </a>
        </figure>
      </div>
      <a
        href="https://vouch.jordanurbs.com/embed/wall-of-love"
        target="_blank"
        rel="noopener noreferrer"
        class="vouch-wall-link"
      >
        Read more student stories <i class="fas fa-arrow-right"></i>
      </a>
    </section>

    <section class="pricing" id="pricing">
      <h2>Start Building Now!</h2>

      <!-- Academy redirect (Jul 26, 2026): direct checkout retired, challenge now ships inside the Academy -->
      <div class="academy-redirect">
        <div class="academy-redirect-badge">
          <i class="fas fa-graduation-cap"></i>
          <span>Now in the Academy</span>
        </div>
        <h3>The Challenge is now available in the full Academy</h3>
        <p class="academy-redirect-copy">
          7-day free trial to see if it's for you.
        </p>
        <a
          href="https://skool.com/aicaptains"
          target="_blank"
          rel="noopener noreferrer"
          class="cta-button academy-redirect-cta"
        >
          <i class="fas fa-rocket"></i> Start Your Free Trial
        </a>
      </div>

      <!-- Payment Success State -->
      <div v-if="false && paymentStatus === 'success'" class="success-state">
        <div class="success-icon">
          <i class="fas fa-check"></i>
        </div>
        <h4>Payment Successful!</h4>
        <p>Thank you for purchasing the Content Commander Challenge! You'll see a charge from AI CAPTAINS LLC.</p>
        <p v-if="includeUpsell" class="upsell-confirmation">
          <div class="upsell-icon">
            <i class="fas fa-graduation-cap"></i>
          </div>
          <div class="upsell-text">
            You've also unlocked the <strong>Full AI Captains Academy - All 9 Courses!</strong>
          </div>
        </p>
        <p>You'll soon receive a confirmation email with everything you need to get started! Godspeed.</p>
        <div class="success-buttons">
          <a 
            href="https://github.com/jordanurbs/terminal-navigation-toolkit.git" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="cta-button download-link"
          >
            <i class="fab fa-github"></i>
            Download from Github
          </a>
          <a 
            href="https://toolkit.ai-captains.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="cta-button download-link"
          >
            <i class="fas fa-play"></i>
            Start Online
          </a>
        </div>
      </div>

      <!-- Payment Failed State -->
      <div v-else-if="false && paymentStatus === 'failed'" class="failed-state">
        <div class="failed-icon">
          <i class="fas fa-times"></i>
        </div>
        <h4>Payment Failed</h4>
        <p>{{ error }}</p>
        <p>Please try again or contact me directly at jordan@aicaptains.ai</p>
        <div class="button-group">
          <button class="secondary-button" @click="resetPayment">
            <i class="fas fa-times"></i> Cancel
          </button>
          <button class="cta-button" @click="resetPayment">
            <i class="fas fa-redo"></i> Try Again
          </button>
        </div>
      </div>

      <!-- Default Payment Form State (retired Jul 26, 2026 — kept for reference/rollback) -->
      <div v-else-if="false">
        <div class="payment-container">
          <div class="order-summary">
            <div class="product-header">
              <h4>Order Summary</h4>
              <div class="limited-offer-badge">
                <i class="fas fa-fire"></i> Limited Time
              </div>
            </div>
            <div class="product-details">
              <span>
                <i class="fas fa-terminal product-icon"></i>
                Content Commander Challenge v2
              </span>
            </div>
            <div class="price-comparison">
              <span class="original-price">$99</span>
              <span class="sale-price">${{ formattedPrice }}</span>
              <span class="savings-badge">Save 60%</span>
            </div>
            <div class="features-list">
              <div class="feature-item">
                <i class="fas fa-key"></i>
                <span>Lifetime Personal API Key for the challenge</span>
              </div>

              <div class="feature-item">
                <i class="fas fa-anchor"></i>
                <span>24/7 AI Support (with Captain JAX!)</span>
              </div>
              <div class="feature-item bonus-feature">
                <i class="fas fa-gift"></i>
                <span><strong>FREE BONUS:</strong> Your AI Foundation Jumpstart Course ($97 value)</span>
              </div>

            </div>
            <div class="total">
              <strong><i class="fas fa-tag"></i> Total:</strong>
              <strong>${{ formattedPrice }}</strong>
            </div>
          </div>

           <div class="payment-form">
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="email">
                  <i class="fas fa-envelope"></i> Email address
                </label>
                <input 
                  type="email" 
                  id="email" 
                  v-model="email" 
                  class="form-input"
                  required
                  placeholder="Enter your email"
                  :disabled="loading"
                />
                <small class="form-help">
                  <i class="fas fa-info-circle"></i>
                  We'll send your API key to this email
                </small>
              </div>
              <div id="card-element"></div>
              <div id="card-errors" role="alert" v-if="error" class="error-message">
                <i class="fas fa-exclamation-circle"></i> {{ error }}
              </div>
              <button 
                type="submit" 
                class="pay-button" 
                :disabled="loading || !stripe"
              >
                <span v-if="loading">
                  <span class="spinner"></span>
                  Processing...
                </span>
                <span v-else>
                  <i class="fas fa-lock"></i> Pay Now
                </span>
              </button>
              <div class="stripe-powered">
                <img src="/images/stripe-white.svg" alt="Powered by Stripe" />
              </div>
            </form>
          </div>

        </div>
      </div>
    </section>

    <!--
      Reframed 2026-07-26: was a "buy CCC, get Jumpstart free ($97 value)" bonus tied to
      the retired direct checkout. The Challenge now ships inside the Academy, so this
      reads as "also included" rather than a purchase incentive.
    -->
    <section class="free-bonus">
      <div class="bonus-badge">
        <i class="fas fa-layer-group"></i>
        <span>ALSO INCLUDED</span>
      </div>
      <div class="bonus-content">
        <h3>"Your AI Foundation Jumpstart" Course</h3>
        <p class="bonus-offer">Your Academy membership includes <strong>Your AI Foundation Jumpstart</strong> alongside the Challenge — so you can build the AI fundamentals and the command line skills together.</p>
        <div class="bonus-image">
          <img src="/images/module-0-min.png" alt="Your AI Foundation Jumpstart Course" />
        </div>
        <div class="bonus-proposition">
          <div class="prop-columns">
            <div class="prop-col">
              <i class="fas fa-robot"></i>
              <strong>AI Jumpstart</strong>
              <span>Prompt & direct AI tools</span>
            </div>
            <div class="prop-operator">+</div>
            <div class="prop-col">
              <i class="fas fa-terminal"></i>
              <strong>Content Commander</strong>
              <span>Terminal, Git & APIs</span>
            </div>
          </div>
          <div class="prop-result">
            <i class="fas fa-equals"></i>
            <span>Build AI-powered apps from scratch</span>
          </div>
        </div>
        <div class="bonus-value">
          <span class="value-label">Both included with</span>
          <span class="value-amount">your Academy membership</span>
        </div>
      </div>
    </section>

    <section class="faq">
      <h2>Frequently Asked Questions</h2>
      <div class="faq-accordion">
        <div 
          v-for="(faq, index) in faqs" 
          :key="index" 
          class="faq-item"
          :class="{ 'active': activeFaq === index }"
        >
          <div 
            class="faq-question" 
            @click="toggleFaq(index)"
          >
            <h3>{{ faq.question }}</h3>
            <i class="fas" :class="activeFaq === index ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </div>
          <div 
            class="faq-answer"
            :style="{ maxHeight: activeFaq === index ? faq.answer.split('</p>').length * 100 + 'px' : '0' }"
          >
            <div v-html="faq.answer"></div>
          </div>
        </div>
      </div>
    </section>

    <section class="final-cta">
      <h2>Ready to Start Vibe Coding?</h2>
      <p class="subtitle">Smash the roadblocks. If you can imagine it, you can build it.</p>
      <button @click="scrollToPricing($event)" class="cta-button"><i class="fas fa-rocket"></i> Start Building Now</button>
    </section>
    </div>

    <CheckoutModal 
      :show="showCheckoutModal"
      @close="showCheckoutModal = false"
    />

    <UpsellModal
      :show="showUpsellModal"
      :loading="upsellLoading"
      @skip="handleSkipUpsell"
      @accept="handleAcceptUpsell"
    />

    <ConfirmationModal
      :show="showConfirmationModal"
      :loading="confirmationLoading"
      :total="totalPrice"
      :includeUpsell="includeUpsell"
      @cancel="resetPayment"
      @confirm="finalizePayment"
    />

    <LoadingOverlay
      :show="processingPayment"
      :message="processingMessage"
      :total="processingTotal"
    />
  </div>
</template>

<script>
import CheckoutModal from '../components/CheckoutModal.vue';
import UpsellModal from '../components/UpsellModal.vue';
import ConfirmationModal from '../components/ConfirmationModal.vue';
import LoadingOverlay from '../components/LoadingOverlay.vue';
import { trackEvent, trackOrder } from '../utils/tracking';
import { loadStripe } from '@stripe/stripe-js';

export default {
  name: 'SalesPage',
  components: {
    CheckoutModal,
    UpsellModal,
    ConfirmationModal,
    LoadingOverlay
  },
  data() {
    return {
      showCheckoutModal: false,
      // Academy upsell disabled for now (Jul 26, 2026) — flip to true to restore the upsell modal
      upsellEnabled: false,
      showUpsellModal: false,
      showConfirmationModal: false,
      upsellLoading: false,
      confirmationLoading: false,
      processingPayment: false,
      processingMessage: 'Processing payment...',
      processingTotal: 0,
      // Baked from vouch.jordanurbs.com/api/wall/wall-of-love on 2026-07-26.
      // Only the Content Commander Challenge testimonials; re-fetch by id to refresh.
      testimonials: [
        {
          id: '21c5dcb5-16f4-4696-b1bd-d6893bd42c24',
          author: 'Desmond',
          title: 'Educator',
          rating: 5,
          avatar: null,
          videoUrl: null,
          body: [
            'I jumped into Jordan’s 30-Day Challenge with little to no expectations.',
            'There are a ton of tutorials online, mostly on YouTube, that purport to “teach” vibe coding but do not really do that. I took a leap of faith and can only say it is a high-value tutorial. A little short on detail, but to his credit, it is advertised as a fundamental course.',
            'I have tried many of the major Agentic AI coding tools with varying degrees of success. Jordan’s course focuses on the Command Line Interface which gives one more granular control over whatever tool you might be using.',
            'I would recommend this course to anyone who wants some more behind-the-scenes control on their vibe coding journey. While it may be just the fundamentals, it has a gradual increase in complexity of tasks and it does not spoon-feed you.',
            'You will put in his recommended commands and will still encounter errors which you must prompt the “Agent” on your own to fix. The best way to learn, in my humble opinion.',
            'Great job, Jordan; I can’t wait to see what’s next.'
          ]
        },
        {
          id: '87dafafb-3d31-4123-b911-4a6fae5aca0e',
          author: 'Gerry Albert',
          title: 'Online Builder',
          rating: 5,
          avatar: null,
          videoUrl: null,
          body: [
            'I am glad I found this course (Content Commander Challenge). I didn’t know how to get started with “vibe coding” tools. It was definitely worth every minute spent.',
            'Here’s my take:',
            'Pros:',
            '• You learn by doing. Gives you good experience in the CLI.',
            '• Enough instruction, if you pay attention, but not so much that you don\'t have to figure anything out yourself.',
            '• Course segments are a good size to do several a day or just one if that\'s all the time you have.',
            '• In-depth introduction to Cursor (and similar AI coding tools).',
            '• Each segment builds on the previous one to show you the project\'s structure.',
            '• I was immediately able to use what I learned to build my own project.',
            'Cons:',
            '• I don’t have any cons…'
          ]
        },
        {
          id: '9c00c2c0-abc4-4038-abbd-af5ccec31f45',
          author: 'Peter Duffy',
          title: 'SaaS Entrepreneur',
          rating: 5,
          avatar: 'https://vouch.jordanurbs.com/media/c50f5088-3c8a-4771-9d23-334888a842fe.png',
          videoUrl: 'https://vouch.jordanurbs.com/v/9c00c2c0-abc4-4038-abbd-af5ccec31f45',
          body: [
            'Just wanted to say thank you very much for the Content Commander challenge. It\'s really good and it\'s really empowered me to learn how to use Cursor together with Supabase and other related tools so that I can build the directory that I\'ve been looking to build. So thank you very much.'
          ]
        }
      ],
      activeFaq: null,
      faqs: [
        {
          question: 'Is this a coding course?',
          answer: '<p><strong>NO!</strong> This is to learn to work WITH code, not become a programmer.</p><p>AI agents will do all the coding for you, but you still have to learn how to manage the AI agent. It\'s like learning to drive without needing to understand how to build the car.</p>'
        },
        {
          question: 'Who is this for?',
          answer: '<p>No-coders, non-developers, creators, solopreneurs, and all the other builders who haven\'t had the time to become a full stack developer, but want to take their technical proficiency to the next level.</p><p>I recommend this for any entrepreneur who knows they will one day want to craft their own AI agents or agentic software, and in general anyone who struggles with resistance when faced with a terminal or command line interface.</p>'
        },
        {
          question: 'What exactly will I learn?',
          answer: '<p>You\'ll learn the minimal technical foundation needed to:</p><ul><li>Navigate the command line with confidence</li><li>Understand how APIs work</li><li> Connect AI services to projects</li><li>Read and modify basic code structures</li><li>Deploy your own simple tools and automations</li></ul>'
        },
        {
          question: 'Do I have to do this in 30 days or can I do it faster?',
          answer: '<p>You can skip ahead as fast as you\'d like—even do it in one day! The 30-day structure is a just a simple way of diving the tasks, but you are free to move at your own pace.</p><p>Some people prefer the gradual approach, while others want to dive in deep. Choose your own adventure.</p>'
        },
        {
          question: 'Will the content be accessible online after the 30 day challenge period?',
          answer: '<p>Yes, the content will be accessible after the 30-days.</p><p>The Challenge lives inside the AI Captains Academy, so you can come back to it as much as you want for as long as your membership is active — and go at whatever pace suits you.</p>'
        },
        {
          question: 'What is the commitment level?',
          answer: '<p>You could do this in 30 days or you could probably do it in 3. Or even less.</p><p>Ideally, this challenge is just enough to get you habitually using the command line so you\'re comfortable with it. That\'s the real goal – getting cozy with the unfamiliar.</p>'
        },
        {
          question: 'Will this work on my computer?',
          answer: '<p>Yes! The Content Commander works on Mac, Windows, and Linux.</p>'
        },
        {
          question: 'I already use some no-code tools. Why do I need this?',
          answer: '<p>No-code tools are great until they\'re not. When you hit their limitations, you\'re stuck.</p><p>This isn\'t about replacing those tools – it\'s about giving you the freedom to go beyond them when needed. Think of it as the difference between riding as a passenger or being able to drive yourself.</p>'
        },
        {
          question: 'What if I get stuck?',
          answer: '<p>That\'s the beauty of the AI Captains community. When you get stuck (and you will – that\'s part of the journey), you\'ll have a group of fellow builders in the Academy to help you through it.</p><p>Plus, I\'ll be sharing the most common roadblocks and how to overcome them based on previous students\' experiences.</p>'
        },
        {
          question: 'Is this just for content creators?',
          answer: '<p>While the tool you\'ll build focuses on content workflows, the skills you\'ll build apply to any digital creator or entrepreneur who wants more technical capability.</p><p>The command line skills you develop will transfer to any technical project you tackle in the future.</p>'
        },
        {
          question: 'What makes this different from other technical courses?',
          answer: '<p>Most technical courses try to teach you everything about coding.</p><p>This isn\'t that.</p><p>Content Commander focuses on the minimal viable technical knowledge you need to direct AI effectively. It\'s practical, focused, and built for people who want results without becoming developers.</p><p>Plus, you\'re actually building something useful as you learn.</p>'
        },
        {
          question: 'Will this become outdated as AI advances?',
          answer: '<p>The opposite, actually. As AI advances, the divide between those who can technically direct it and those who cannot will only grow wider.</p><p>The foundation you build here will give you the ability to adapt to and leverage new AI tools as they emerge, rather than being limited to their consumer-facing interfaces.</p>'
          
        },
        {
          question: 'Are there any paid services I will need to sign up for in order to follow along? If so, how much do they cost?',
          answer: '<p>No paid services are required — you can literally do this challenge on the command line terminal if you want!</p><p>The only paid service I recommend is optional, and that\'s Cursor AI. It gives you a 2-week trial ($20/mo after), and that is more than enough time to finish the 30-day challenge.</p><p>I recommend Cursor for anyone learning to make software with AI... using it with the CCC will help you gain familiarity with it!</p><p>You could also use <a href="https://code.visualstudio.com/" target="_blank" rel="noopener noreferrer">Visual Studio Code</a> for free. It\'s not the same as Cursor\'s AI Agent, but it\'s the same interface for project infrastructure, which is another core learning objective of this challenge.</p>'
        },
        {
          question: 'But I need an API key for the AI part of the build, right? How much does that cost and how do I get them?',
          answer: '<p>You\'ll need an OpenAI or Anthropic API key to get the Content Commander to function. But in truth, you won\'t actually need it to accomplish the Content Commander build itself--only to use it!</p><p>Visit <a href="https://anthropic.com/api" target="_blank" rel="noopener noreferrer">Anthropic</a>\'s developer console</a> or <a href="https://openai.com/api" target="_blank" rel="noopener noreferrer">OpenAI</a>\'s developer console to charge up on API credits. You can pay as little as $5 and you\'ll have plenty left over after getting good use out of the Content Commander app!</p>'
        },
      ],
      paymentStatus: null,
      error: null,
      email: '',
      loading: false,
      stripe: null,
      elements: null,
      card: null,
      paymentMethodId: null,
      includeUpsell: false
    }
  },
  computed: {
    price() {
      // Get price from environment variable with a fallback
      return parseInt(import.meta.env.VITE_CONTENT_COMMANDER_PRICE) || 3900;
    },
    formattedPrice() {
      // Format the price as dollars and cents
      return (this.price / 100).toFixed(2);
    },
    upsellPrice() {
      // Get upsell price from environment variable with a fallback
      return parseInt(import.meta.env.VITE_ACADEMY_UPSELL_PRICE) || 24900;
    },
    totalPrice() {
      // Calculate total price including upsell if accepted
      return this.includeUpsell ? this.price + this.upsellPrice : this.price;
    }
  },
  mounted() {
    // Add Font Awesome CDN
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css';
    document.head.appendChild(link);

    // Initialize Stripe when component is mounted
    this.initializeStripe();

    // Add scroll event listener for parallax effect
    this.handleScroll = () => {
      const scrolled = window.pageYOffset;
      const background = document.querySelector('.hero-background');
      if (background) {
        background.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };

    window.addEventListener('scroll', this.handleScroll);
  },
  methods: {
    async initializeStripe() {
      try {
        // Get the Stripe key from environment
        const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
        
        if (!stripeKey) {
          console.error('Stripe key is missing');
          throw new Error('Payment system configuration error');
        }

        // Log key information (safely)
        console.log('Stripe configuration:', {
          keyType: stripeKey.startsWith('pk_test_') ? 'test' : 'live',
          keyPrefix: stripeKey.substring(0, 7),
          keyLength: stripeKey.length,
          environment: import.meta.env.MODE
        });

        // Initialize Stripe
        this.stripe = await loadStripe(stripeKey);
        
        if (!this.stripe) {
          throw new Error('Failed to initialize Stripe');
        }

        // Create Elements instance with explicit appearance
        this.elements = this.stripe.elements({
          locale: 'en',
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#FFD23F',
              colorBackground: '#363636',
              colorText: '#e0e0e0',
              colorDanger: '#ff6b6b',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px'
            },
            rules: {
              '.Input': {
                color: '#e0e0e0',
                backgroundColor: '#363636',
                border: '1px solid #3d3d3d'
              },
              '.Input:focus': {
                border: '1px solid #FFD23F',
                boxShadow: '0 0 0 2px rgba(65, 216, 246, 0.25)'
              },
              '.Input::placeholder': {
                color: '#888'
              }
            }
          }
        });
        
        // Use nextTick to ensure the DOM element exists
        this.$nextTick(() => {
          if (!this.card) {
            this.card = this.elements.create('card', {
              style: {
                base: {
                  fontSize: '16px',
                  color: '#e0e0e0',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                  '::placeholder': {
                    color: '#87868c'
                  },
                  iconColor: '#FFD23F',
                  ':-webkit-autofill': {
                    color: '#e0e0e0',
                    backgroundColor: '#363636'
                  },
                },
                invalid: {
                  color: '#ff6b6b',
                  iconColor: '#ff6b6b'
                }
              },
              hidePostalCode: true
            });
            
            // Mount the card element
            const cardElement = document.getElementById('card-element');
            if (cardElement) {
              console.log('Mounting card element...');
              this.card.mount('#card-element');
              
              this.card.on('change', (event) => {
                console.log('Card element change:', event);
                if (event.error) {
                  this.error = event.error.message;
                } else {
                  this.error = null;
                }
              });
            } else {
              // Expected since 2026-07-26: the checkout form is retired in favour of the
              // Academy free-trial CTA, so #card-element no longer renders. Not an error.
              console.log('Card element container not present (checkout retired) — skipping Stripe card mount.');
            }
          }
        });
      } catch (err) {
        console.error('Error initializing Stripe:', err);
        this.error = 'Failed to initialize payment form. Please try again.';
      }
    },
    async handleSubmit() {
      this.loading = true;
      this.error = null;
      
      if (!this.email) {
        this.error = 'Please enter your email address';
        this.loading = false;
        return;
      }
      
      try {
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.card,
          billing_details: {
            email: this.email
          }
        });

        if (error) {
          this.error = error.message;
          this.paymentStatus = 'failed';
          this.loading = false;
          return;
        }

        console.log('Payment method created:', paymentMethod.id);

        // Store the payment method for later use with the upsell
        this.paymentMethodId = paymentMethod.id;
        
        this.loading = false;
        if (this.upsellEnabled) {
          // Show upsell modal instead of creating payment intent immediately
          this.showUpsellModal = true;
        } else {
          // Upsell disabled: go straight to the standard confirmation
          this.includeUpsell = false;
          this.showConfirmationModal = true;
        }
      } catch (err) {
        console.error('Payment error:', err);
        this.error = err.message || 'An error occurred. Please try again.';
        this.paymentStatus = 'failed';
        this.loading = false;
      }
    },
    resetPayment() {
      this.error = null;
      this.paymentStatus = null;
      this.loading = false;
      this.email = '';
      this.paymentMethodId = null;
      this.showConfirmationModal = false;
      this.confirmationLoading = false;
      
      // Unmount and clear the existing card element
      if (this.card) {
        this.card.unmount();
        this.card = null;
      }
      
      // Reinitialize the card element
      this.$nextTick(() => {
        this.initializeStripe();
      });
    },
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    },
    scrollToPricing(event) {
      event.preventDefault();
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    },
    // Handle when user skips the upsell
    async handleSkipUpsell() {
      // Track event
      trackEvent('Upsell', 'Declined', 'Academy Upgrade');
      
      // Show loading state in upsell modal briefly before hiding
      this.upsellLoading = true;
      
      // Close modal after a short delay for visual feedback
      setTimeout(() => {
        this.showUpsellModal = false;
        this.includeUpsell = false;
        this.showConfirmationModal = true;
      }, 300);
    },
    
    // Handle when user accepts the upsell
    async handleAcceptUpsell() {
      // Track event
      trackEvent('Upsell', 'Accepted', 'Academy Upgrade', this.upsellPrice / 100);
      
      // Show loading state in upsell modal
      this.upsellLoading = true;
      
      // Close modal after a short delay for visual feedback
      setTimeout(() => {
        this.showUpsellModal = false;
        this.includeUpsell = true;
        this.showConfirmationModal = true;
      }, 300);
    },
    
    // Final confirmation before processing payment
    async finalizePayment() {
      this.confirmationLoading = true;
      
      // Close confirmation modal after a short delay for visual feedback
      setTimeout(() => {
        this.showConfirmationModal = false;
        this.processPayment(this.includeUpsell);
      }, 300);
    },
    
    // Process the payment with or without upsell
    async processPayment(includeUpsell) {
      // Calculate total amount
      const totalAmount = includeUpsell ? this.price + this.upsellPrice : this.price;
      this.processingTotal = totalAmount;
      
      // Show the processing overlay
      this.processingPayment = true;
      this.processingMessage = includeUpsell 
        ? 'Processing your order...' 
        : 'Processing your order...';
      
      try {
        // Add debug logging for the payment method ID
        console.log('Using payment method ID:', this.paymentMethodId);
        
        // Make sure we have a valid payment method ID
        if (!this.paymentMethodId || typeof this.paymentMethodId !== 'string' || !this.paymentMethodId.startsWith('pm_')) {
          throw new Error('Invalid payment method. Please try again with a new card.');
        }
        
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            paymentMethodId: this.paymentMethodId,
            amount: totalAmount,
            email: this.email,
            includeUpsell: includeUpsell
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Payment intent result:', result);

        if (result.error) {
          this.error = result.error;
          this.paymentStatus = 'failed';
          
          // Track failed payment
          trackEvent('Purchase', 'Failed', this.error);
        } else if (result.status === 'succeeded') {
          this.paymentStatus = 'success';
          this.error = null;
          
          // Track e-commerce order
          trackOrder({
            orderId: result.clientSecret.split('_secret_')[0],
            total: totalAmount / 100, // Convert cents to dollars
            discount: 0
          });
          
          // Track conversion event
          trackEvent(
            'Purchase',
            'Completed',
            includeUpsell ? 'Content Commander with Academy' : 'Content Commander',
            totalAmount / 100
          );
        } else {
          this.error = 'Payment failed. Please try again.';
          this.paymentStatus = 'failed';
          
          // Track failed payment
          trackEvent('Purchase', 'Failed', this.error);
        }
      } catch (err) {
        console.error('Payment error:', err);
        this.error = err.message || 'An error occurred. Please try again.';
        this.paymentStatus = 'failed';
        
        // Track failed payment
        trackEvent('Purchase', 'Failed', this.error);
      } finally {
        // Hide the processing overlay
        this.processingPayment = false;
        this.loading = false;
        this.upsellLoading = false;
      }
    }
  },
  beforeUnmount() {
    if (this.card) {
      this.card.unmount();
      this.card = null;
    }
    // Remove scroll event listener
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style scoped>
.sales-page-wrapper {
  font-family: 'Inter', sans-serif;
  background-color: #1a1a1a;
  color: #e0e0e0;
  overflow-x: hidden;
  position: relative;
}

.sales-page-content {
  padding-top: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 2rem;
  padding-right: 2rem;
}

body {
  overflow-x: hidden;
}

a {
  color: #ffffff;
  text-decoration: none;
  transition: color 0.2s ease;
}

a:hover {
  color: #FFD23F;
}

.hero {
  position: relative;
  width: 100vw;
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  margin-bottom: 8rem;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, #1a1a1a, rgba(26, 26, 26, 0.65));
  z-index: 1;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('/images/ILOVE2.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 1000px;
  padding: 0 2rem;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-logo {
  width: 120px;
  height: auto;
  margin-bottom: 2rem;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
}

.hero-aica-logo {
  height: 70px;
  width: auto;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.hero h1 {
  font-size: 3rem;
  margin-bottom: 2rem;
  color: white;
  line-height: 1.3;
  font-weight: 700;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.subtitle {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 1.5rem;
  line-height: 1.4;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.subtitle-secondary {
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 3rem;
  line-height: 1.4;
  font-style: italic;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.command-line-to-confidence {
  color: #FFD23F; 
  font-weight: 700;
  text-shadow: 
    0 0 5px rgba(255, 107, 53, 0.8),   /* Inner glow */
    0 0 15px rgba(255, 107, 53, 0.6),  /* Medium glow */
    0 0 25px rgba(255, 107, 53, 0.3),  /* Outer glow */
    0 0 35px rgba(255, 210, 63, 0.2);  /* Yellow tint outer glow */
 font-size:1.5em;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(45deg, #FF6B35 0%, #FFD23F 100%);
  color: #1a1a1a;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  font-size: 1.4em;
}

.hero-cta { font-size: 1.4em; } 

.cta-button:hover {
  background: ##FFD23F;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

section {
  margin: 8rem 0;
}

h2 {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #FFD23F;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
}

.feature-card {
  background: #2d2d2d;
  padding: 3rem 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  border: 1px solid #3d3d3d;
}

.feature-card h3 {
  color: #e0e0e0;
  margin: 1rem 0;
  font-size: 1.3rem;
}

.feature-card p {
  color: #b0b0b0;
}

.feature-card:hover {
  transform: translateY(-5px);
  border-color: #FFD23F;
}

.timeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
}

.timeline-item {
  text-align: center;
  padding: 3rem 2rem;
  background: #2d2d2d;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  border: 1px solid #3d3d3d;
}

.timeline-item h3 {
  color: #e0e0e0;
  margin: 1rem 0;
  font-size: 1.3rem;
}

.timeline-item p {
  color: #b0b0b0;
}

.timeline-item:hover {
  transform: translateY(-5px);
  border-color: #FFD23F;
}

.price-card {
  background: #2d2d2d;
  padding: 4rem 3rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  margin: 0 auto;
  border: 1px solid #3d3d3d;
}

.price-card h3 {
  color: #e0e0e0;
  margin: 1rem 0;
  font-size: 1.3rem;
}

.price-card ul {
  list-style: none;
  padding: 0;
  margin: 3rem 0;
}

.price-card li {
  margin: 1.5rem 0;
  color: #b0b0b0;
  font-size: 1.1rem;
}

.price-card li i {
  color: #FFD23F;
  margin-right: 0.5rem;
}

.price {
  font-size: 3.5rem;
  font-weight: bold;
  color: #FFD23F;
  margin: 1.5rem 0;
}

.price-icon {
  font-size: 3rem;
  color: #FFD23F;
  margin-bottom: 1.5rem;
}

.faq-accordion {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: #2d2d2d;
  border: 1px solid #3d3d3d;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  cursor: pointer;
  background: #363636;
  transition: background-color 0.3s ease;
}

.faq-question:hover {
  background: #404040;
}

.faq-question h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #e0e0e0;
}

.faq-question i {
  color: #FFD23F;
  transition: transform 0.3s ease;
}

.faq-answer {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease-in-out;
  background: #2d2d2d;
}

.faq-answer > div {
  padding: 0 2rem 2rem 2rem;
  color: #e0e0e0;
}

.faq-answer > div a {
  color: #ffffff !important;
  text-decoration: underline !important;
  font-weight: 600 !important;
}

.faq-answer > div a:hover {
  color: #FFD23F !important;
}

.faq-item.active .faq-question {
  background: #FFD23F;
  color: #1a1a1a;
}

.faq-item.active .faq-question h3 {
  color: #1a1a1a;
}

.faq-item.active .faq-question i {
  color: #1a1a1a;
  transform: rotate(180deg);
}

.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: transparent;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  border-bottom: 1px solid rgba(61, 61, 61, 0.5);
}

.nav-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-weight: bold;
  font-size: 1.2rem;
  color: #e0e0e0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logo-image {
  height: 44px;
  width: auto;
}

.logo i {
  margin-right: 0.5rem;
  color: #FFD23F;
}

.nav-cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(90deg, #FF4500 0%, #FF6B35 30%, #FFA500 70%, #FFD700 100%);
  color: #1a1a1a;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  -webkit-appearance: none;
  appearance: none;
}

.nav-cta-button:hover {
  background: linear-gradient(90deg, #DC2626 0%, #FF6B35 40%, #FFD23F 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.nav-cta-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.feature-icon, .timeline-icon {
  font-size: 2.5rem;
  color: #FFD23F;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .hero {
    height: auto;
    min-height: 500px;
    padding: 4rem 0;
  }
  
  .hero-aica-logo {
    height: 50px;
    margin-bottom: 1rem;
  }
  
  .hero h1 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
  
  .subtitle {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
  
  .subtitle-secondary {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
  
  section {
    margin: 6rem 0;
  }
  
  h2 {
    font-size: 2rem;
    margin-bottom: 2rem;
  }

  .feature-grid,
  .timeline {
    gap: 2rem;
  }

  .feature-card,
  .timeline-item {
    padding: 2rem 1.5rem;
  }

  .price-card {
    padding: 3rem 2rem;
  }

  .nav-content {
    padding: 0.75rem 1rem;
  }

  .nav-cta-button {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }

  .logo {
    font-size: 1rem;
  }
}

/* Payment Form Styles */
.payment-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 850px;
  margin: 0 auto;
  align-items: center;
}

@media (max-width: 768px) {
  .payment-container {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .order-summary,
  .payment-form {
    width: 100%;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
}

.order-summary {
  margin: 0;
  height: fit-content;
  background: #2d2d2d;
  border-radius: 12px;
  border: 1px solid #3d3d3d;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.payment-form {
  margin: 0;
  height: fit-content;
  background: #2d2d2d;
  border-radius: 12px;
  border: 1px solid #3d3d3d;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.product-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #3d3d3d;
}

.product-header h4 {
  color: #e0e0e0;
  font-size: 1.5rem;
  margin: 0;
}

.limited-offer-badge {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  animation: firePulse 1.5s ease-in-out infinite;
}

.limited-offer-badge i {
  font-size: 0.7rem;
}

@keyframes firePulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.02); }
}

/* Academy redirect (Jul 26, 2026) — replaces the retired direct checkout */
.academy-redirect {
  max-width: 640px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
  text-align: center;
  background: rgba(255, 210, 63, 0.06);
  border: 1px solid rgba(255, 210, 63, 0.3);
  border-radius: 12px;
}

.academy-redirect-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.9rem;
  margin-bottom: 1.5rem;
  background: rgba(255, 210, 63, 0.15);
  border: 1px solid rgba(255, 210, 63, 0.4);
  border-radius: 999px;
  color: #FFD23F;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.academy-redirect h3 {
  margin: 0 0 1rem;
  font-size: 1.85rem;
  line-height: 1.25;
  color: #fff;
}

.academy-redirect-copy {
  margin: 0 0 2rem;
  font-size: 1.15rem;
  color: #d1d5db;
}

.academy-redirect-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

@media (max-width: 640px) {
  .academy-redirect {
    padding: 2rem 1.25rem;
  }

  .academy-redirect h3 {
    font-size: 1.4rem;
  }

  .academy-redirect-copy {
    font-size: 1rem;
  }
}

.price-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 1rem 0;
  padding: 1rem;
  background: rgba(255, 210, 63, 0.1);
  border-radius: 8px;
  border: 1px dashed rgba(255, 210, 63, 0.3);
}

.original-price {
  color: #6b7280;
  font-size: 1.25rem;
  text-decoration: line-through;
  opacity: 0.7;
}

.sale-price {
  color: #FFD23F;
  font-size: 2rem;
  font-weight: 700;
}

.savings-badge {
  background: #22c55e;
  color: #fff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
}

.bonus-feature {
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 6px;
  padding: 0.75rem !important;
  margin-top: 0.5rem;
}

.bonus-feature i {
  color: #4ade80 !important;
}

.bonus-feature strong {
  color: #4ade80;
}

.product-icon {
  font-size: 1.5rem;
  color: #FFD23F;
}

.features-list {
  margin: 1.5rem 0;
  padding: 1.5rem;
  background: #363636;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  display: flex;
  flex-direction: column;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: #e0e0e0;
  line-height: 1.4;
}

.feature-item:last-child {
  margin-bottom: 0;
}

.feature-item i {
  color: #FFD23F;
  min-width: 1rem;
  margin-top: 0.2rem;
}

.feature-item span {
  flex: 1;
}

.product-details {
  display: flex;
  justify-content: space-between;
  margin: 1.5rem 0;
  align-items: center;
  color: #e0e0e0;
  font-size: 1.2rem;
}

.product-details span:first-child {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.product-details i {
  color: #FFD23F;
  font-size: 1.2rem;
}

.total {
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #3d3d3d;
  color: #e0e0e0;
  font-size: 1.2rem;
}

.total i {
  margin-right: 0.5rem;
  color: #FFD23F;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #3d3d3d;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  background: #363636;
  color: #e0e0e0;
}

.form-input:focus {
  outline: none;
  border-color: #FFD23F;
  box-shadow: 0 0 0 2px rgba(65, 216, 246, 0.25);
}

.form-input:disabled {
  background: #2d2d2d;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #888;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #e0e0e0;
}

.form-help {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #888;
}

#card-element {
  padding: 1rem;
  border: 1px solid #3d3d3d;
  border-radius: 8px;
  background: #363636;
}

.error-message {
  color: #ff6b6b;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pay-button {
  width: 100%;
  padding: 1rem;
  background: #FFD23F;
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  margin-top: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: bold;
}

.pay-button:hover {
  background: ##FFD23F;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.pay-button:disabled {
  background: #666;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Success State Styles */
.success-state {
  text-align: center;
  padding: 3rem 2rem;
  max-width: 500px;
  margin: 0 auto;
  background: #2d2d2d;
  border-radius: 12px;
  border: 1px solid #3d3d3d;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.success-icon {
  width: 64px;
  height: 64px;
  background: #FFD23F;
  color: #1a1a1a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 1.5rem;
}

.success-state h4 {
  color: #e0e0e0;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.success-state p {
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.upsell-confirmation {
  background: #363636;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border: 1px solid #FFD23F;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  text-align: left;
}

.upsell-icon {
  color: #FFD23F;
  font-size: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upsell-text {
  flex: 1;
  line-height: 1.5;
}

.upsell-confirmation strong {
  color: #FFD23F;
  display: block;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.success-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.success-buttons .cta-button {
  min-width: 200px;
  margin-top: 0;
}

.success-buttons .download-link {
  background: #FFD23F;
  color: #1a1a1a;
  text-decoration: none;
  transition: none;
}

.success-buttons .download-link:hover {
  background: #FFD23F;
  transform: none;
  box-shadow: none;
}

@media (max-width: 768px) {
  .success-buttons {
    flex-direction: column;
    gap: 1rem;
  }
  
  .success-buttons .cta-button {
    width: 100%;
  }
}

/* Failed State Styles */
.failed-state {
  text-align: center;
  padding: 3rem 2rem;
  max-width: 500px;
  margin: 0 auto;
  background: #2d2d2d;
  border-radius: 12px;
  border: 1px solid #3d3d3d;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.failed-icon {
  width: 64px;
  height: 64px;
  background: #ff6b6b;
  color: #1a1a1a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 1.5rem;
}

.failed-state h4 {
  color: #e0e0e0;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.failed-state p {
  color: #e0e0e0;
  margin-bottom: 1.5rem;
}

.button-group {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.secondary-button {
  padding: 1rem 2rem;
  background: #666;
  color: #e0e0e0;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.secondary-button:hover {
  background: #555;
}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(26, 26, 26, 0.3);
  border-radius: 50%;
  border-top-color: #1a1a1a;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Stripe logo styling */
.stripe-powered {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #3d3d3d;
}

.stripe-powered img {
  height: 24px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.stripe-powered img:hover {
  opacity: 1;
}

.hook {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

.hook p {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #b0b0b0;
  font-size: 1.2rem;
}

.hook p:last-child {
  margin-bottom: 0;
}

.final-cta {
  text-align: center;
  padding: 6rem 2rem;
  background: #2d2d2d;
  border-radius: 12px;
  margin: 8rem auto;
  max-width: 800px;
}

.final-cta .cta-button {
  font-size: 1.2rem;
  padding: 1.25rem 3rem;
  margin-top: 2rem;
}

.final-cta h2 {
  margin-bottom: 1rem;
}

.final-cta .subtitle {
  margin-bottom: 2rem;
}

.terminal-output {
  color: #cccccc;
  margin-top: 0.25rem;
  margin-bottom: 1rem;
}

.guarantee {
  background-color: var(--primary);
  color: white;
  padding: 4rem 0;
  text-align: center;
}

.guarantee-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.guarantee-icon {
  font-size: 3rem;
  color: var(--accent);
  margin-bottom: 1.5rem;
}

.guarantee h2 {
  color: white;
  margin-bottom: 1.5rem;
  font-size: 2.5rem;
}

.guarantee p {
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.guarantee-note {
  font-size: 1rem;
  font-style: italic;
  opacity: 0.8;
}

.video-section {
  padding: 4rem 0;
  text-align: center;
}

.video-container {
  max-width: 600px;
  margin: 4rem auto 0;
  padding: 0 2rem;
 
}

.video-thumbnail {
  position: relative;
  display: block;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  aspect-ratio: 16/9;
  margin-bottom: 4rem;
}

.video-thumbnail:hover {
  transform: translateY(-5px);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 12px;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: #FFD23F;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.play-button i {
  color: #1a1a1a;
  font-size: 2.5rem;
  margin-left: 5px;
}

.video-thumbnail:hover .play-button {
  background: #FFD23F;
  transform: translate(-50%, -50%) scale(1.1);
}

@media (max-width: 768px) {
  .video-container {
    padding: 0 1rem;
    max-width: 100%;
  }
  
  .play-button {
    width: 60px;
    height: 60px;
  }
  
  .play-button i {
    font-size: 2rem;
  }
}

.video-frame {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: none;
}

.announcement {
  text-align: center;
  padding: 3rem 2rem;
  background: #2d2d2d;
  border-radius: 12px;
  border: 1px solid #3d3d3d;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.announcement h3 {
  color: #ffffff;
  font-size: 1.5rem;
  line-height: 1.4;
  margin: 0;
}

.testimonials {
  text-align: center;
  margin: 8rem 0;
}

.testimonial-container {
  max-width: 800px;
  margin: 0 auto;
}

/* Vouch testimonial cards (static, replaced the Senja iframe 2026-07-26) */
.testimonial-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.vouch-card {
  margin: 0;
  padding: 2rem;
  text-align: left;
  background: #0d0d0d;
  border-radius: 16px;
  box-shadow: 6px 6px 0 rgba(255, 210, 63, 0.85);
}

.vouch-rating {
  display: flex;
  gap: 0.2rem;
  margin-bottom: 1rem;
  color: #FFD23F;
  font-size: 0.9rem;
}

.vouch-body {
  margin: 0;
  padding: 0;
  border: none;
  quotes: none;
}

.vouch-body p {
  margin: 0 0 1rem;
  color: #f0f0f0;
  font-size: 1.05rem;
  line-height: 1.6;
}

.vouch-body p:last-child {
  margin-bottom: 0;
}

.vouch-author {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.vouch-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.vouch-author-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.vouch-author-name {
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
}

.vouch-author-title {
  color: #9ca3af;
  font-size: 0.85rem;
  font-style: italic;
}

.vouch-video-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1rem;
  color: #FFD23F;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
}

.vouch-video-link:hover {
  text-decoration: underline;
}

.vouch-wall-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
  color: #FFD23F;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
}

.vouch-wall-link:hover {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .vouch-card {
    padding: 1.5rem;
    box-shadow: 4px 4px 0 rgba(255, 210, 63, 0.85);
  }

  .vouch-body p {
    font-size: 1rem;
  }
}

/* Check if an icon class exists and fix it */
.fa-school-flag:before {
  content: "\f549"; /* This is the graduation cap icon code */
}

/* By Day 30 Section Styles */
.by-day-30 {
  margin-top: 4rem;
  text-align: center;
  padding: 3rem 2rem;
  background: #363636;
  border-radius: 12px;
  border: 1px solid #FFD23F;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.by-day-30-text {
  font-size: 1.5rem;
  font-weight: 600;
  color: #FFD23F;
  margin-bottom: 2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.by-day-30-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 500px;
  margin: 0 auto;
}

.by-day-30-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #2d2d2d;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  transition: all 0.3s ease;
}

.by-day-30-item:hover {
  border-color: #FFD23F;
  transform: translateX(5px);
}

.by-day-30-item i {
  color: #FFD23F;
  font-size: 1.2rem;
  min-width: 1.2rem;
}

.by-day-30-item span {
  color: #e0e0e0;
  font-size: 1.1rem;
  font-weight: 500;
  text-align: left;
  flex: 1;
}

@media (max-width: 768px) {
  .by-day-30 {
    margin-top: 3rem;
    padding: 2rem 1.5rem;
  }
  
  .by-day-30-text {
    font-size: 1.3rem;
    margin-bottom: 1.5rem;
  }
  
  .by-day-30-item {
    padding: 0.75rem;
  }
  
  .by-day-30-item span {
    font-size: 1rem;
  }
}

/* FREE BONUS Section */
.free-bonus {
  max-width: 700px;
  margin: 4rem auto;
  padding: 2.5rem;
  background: linear-gradient(135deg, #1a3a1a 0%, #2d5a2d 50%, #1a3a1a 100%);
  border-radius: 16px;
  border: 3px solid #4ade80;
  box-shadow:
    0 0 20px rgba(74, 222, 128, 0.3),
    0 0 40px rgba(74, 222, 128, 0.1),
    0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  animation: bonusPulse 3s ease-in-out infinite;
}

.free-bonus::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(74, 222, 128, 0.1) 50%,
    transparent 70%
  );
  animation: bonusShine 4s linear infinite;
}

@keyframes bonusPulse {
  0%, 100% {
    box-shadow:
      0 0 20px rgba(74, 222, 128, 0.3),
      0 0 40px rgba(74, 222, 128, 0.1),
      0 8px 32px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow:
      0 0 30px rgba(74, 222, 128, 0.5),
      0 0 60px rgba(74, 222, 128, 0.2),
      0 8px 32px rgba(0, 0, 0, 0.3);
  }
}

@keyframes bonusShine {
  0% { transform: translateX(-100%) rotate(45deg); }
  100% { transform: translateX(100%) rotate(45deg); }
}

.bonus-badge {
  position: absolute;
  top: -1px;
  right: 20px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  padding: 0.5rem 1.25rem;
  border-radius: 0 0 12px 12px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

.bonus-badge i {
  font-size: 1rem;
  animation: giftBounce 1s ease-in-out infinite;
}

@keyframes giftBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.bonus-content {
  position: relative;
  z-index: 1;
  text-align: center;
}

.bonus-urgency {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid #ef4444;
  color: #fca5a5;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  animation: urgencyPulse 2s ease-in-out infinite;
}

.bonus-urgency i {
  color: #ef4444;
  animation: clockTick 1s steps(1) infinite;
}

@keyframes urgencyPulse {
  0%, 100% {
    background: rgba(239, 68, 68, 0.2);
    transform: scale(1);
  }
  50% {
    background: rgba(239, 68, 68, 0.35);
    transform: scale(1.02);
  }
}

@keyframes clockTick {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.6; }
}

.free-bonus h3 {
  color: #fff;
  font-size: 1.75rem;
  margin-bottom: 0.75rem;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.bonus-offer {
  color: #d1fae5;
  font-size: 1.05rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;
  max-width: 450px;
  margin-left: auto;
  margin-right: auto;
}

.bonus-offer strong {
  color: #fff;
}

.bonus-image {
  margin: 1.5rem auto;
  max-width: 350px;
}

.bonus-image img {
  width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(74, 222, 128, 0.3);
  transition: transform 0.3s ease;
}

.bonus-image img:hover {
  transform: scale(1.02);
}

.bonus-proposition {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  padding: 1.25rem;
  margin: 1.5rem 0;
  border: 1px solid rgba(74, 222, 128, 0.2);
}

.prop-columns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.prop-col {
  flex: 1;
  max-width: 160px;
  text-align: center;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid rgba(74, 222, 128, 0.15);
}

.prop-col i {
  font-size: 1.5rem;
  color: #4ade80;
  margin-bottom: 0.5rem;
  display: block;
}

.prop-col strong {
  display: block;
  color: #fff;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.prop-col span {
  color: #a7f3d0;
  font-size: 0.8rem;
  line-height: 1.3;
}

.prop-operator {
  font-size: 1.25rem;
  font-weight: 700;
  color: #4ade80;
  flex-shrink: 0;
}

.prop-result {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(22, 163, 74, 0.3) 100%);
  border: 1px solid #4ade80;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-top: 1rem;
}

.prop-result i {
  font-size: 1rem;
  color: #4ade80;
}

.prop-result span {
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
}

@media (max-width: 480px) {
  .prop-columns {
    flex-direction: column;
    gap: 0.5rem;
  }

  .prop-col {
    max-width: 100%;
    width: 100%;
  }

  .prop-operator {
    padding: 0.25rem 0;
  }
}

.free-bonus p {
  color: #d1fae5;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.bonus-value {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.value-label {
  color: #a7f3d0;
  font-size: 1rem;
}

/* Reframed 2026-07-26: no longer a struck-through "$97" price, now names what the
   membership includes — so no line-through / muted treatment. */
.value-amount {
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
}

.value-free {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  padding: 0.4rem 1rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 1px;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

@media (max-width: 768px) {
  .free-bonus {
    margin: 3rem 1rem;
    padding: 2rem 1.5rem;
  }

  .bonus-badge {
    right: 15px;
    padding: 0.4rem 1rem;
    font-size: 0.8rem;
  }

  .free-bonus h3 {
    font-size: 1.4rem;
    margin-top: 1rem;
  }

  .free-bonus p {
    font-size: 1rem;
  }
}
</style> 
